import listboxNav from './listboxNav';

/**
 * Alpine data for Menu:Select — a value-selection listbox with
 * server-rendered options (<twig:Menu:Option> blocks).
 *
 * Composes listboxNav for keyboard navigation. Highlight state is
 * managed declaratively via x-bind:class in the Option template,
 * using dataset.itemIndex assigned during init.
 *
 * Config: { value, label, placeholder, disabled }
 * Dispatches: menu-select { value, label, thumbnail, description }
 *
 * Template: templates/components/Menu/Select.html.twig
 */
export default function menuSelect(config = {}) {
  const nav = listboxNav();

  return {
    ...nav,
    value: config.value || '',
    label: config.label || '',
    thumbnail: '',
    description: '',
    placeholder: config.placeholder || '',
    disabled: config.disabled || false,

    init() {
      this.$nextTick(() => {
        const listbox = this.$refs.listbox;
        if (!listbox) return;

        const prefix = listbox.id?.replace(/-listbox$/, '') || 'menu-select';
        listbox.querySelectorAll('[role="option"]').forEach((item, i) => {
          item.id = `${prefix}-option-${i}`;
          item.dataset.itemIndex = i;
        });

        this._resolveSelectedMeta();
      });

      this.initHighlightScroll();
      this.initPageShowResync();
    },

    _resolveSelectedMeta() {
      if (!this.value || !this.$refs.listbox) {
        this.thumbnail = '';
        this.description = '';
        return;
      }
      const el = this.$refs.listbox.querySelector(
        `[data-value="${CSS.escape(this.value)}"]`,
      );
      this.thumbnail = el?.querySelector('img')?.src || '';
      this.description =
        el
          ?.querySelector('[data-option-description]')
          ?.textContent.trim() || '';
    },

    getItemCount() {
      const items = this.$refs.listbox?.querySelectorAll('[role="option"]');
      return items ? items.length : 0;
    },

    isItemDisabled(index) {
      const items =
        this.$refs.listbox?.querySelectorAll('[role="option"]') || [];
      return items[index]?.hasAttribute('data-disabled') ?? false;
    },

    openDropdown() {
      if (this.disabled || this.open) return;
      this.open = true;
      this.highlightedIndex = this.nextEnabledIndex(0, 1);
    },

    selectHighlighted() {
      const items =
        this.$refs.listbox?.querySelectorAll('[role="option"]') || [];
      if (this.highlightedIndex >= 0 && items[this.highlightedIndex]) {
        items[this.highlightedIndex].querySelector('button')?.click();
      }
    },

    handleKeydown(event) {
      if (this.disabled) return;
      nav.handleKeydown.call(this, event);
    },

    select(value, label) {
      this.value = value;
      this.label = label;
      this._resolveSelectedMeta();
      this.close();
      this.$dispatch('menu-select', {
        value,
        label,
        thumbnail: this.thumbnail,
        description: this.description,
      });
    },
  };
}
